import { createContext, useEffect, useMemo, useState } from "react";
import RefreshToken from "../../hooks/Auth/refreshToken";
import axios from "axios";
import { getTimeZoneFromAllSites } from "../../services/getTimeZoneFromAllsItes";
import { getMapDataObjectFromAllSites } from "../../services/getMapDataObjectFromAllSites";
import { GetBounderiesStorage, GetRoadsStorage, GetPOIS, GetCategories, GetMemos } from "../../services/getAllMapData";
import useStateAndRef from "../../hooks/useStateAndRef";
import { getAllRoadLiveRecordings } from "../../services/getAllRoadLiveRecordings";
import useSockets from "../../hooks/useSockets";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {

  const [auth, setAuth] = useState({});
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relevantSite, setRelevantSite] = useState(null);
  const [sitesMapsDataObject, setSitesMapsDataObject] = useState(null);
  const [relevantMapData, setRelevantMapData] = useState(null);
  const relevantUrl = useMemo(() => {
    if (!relevantSite) return null;
    return relevantSite === 'localhost'
      ? 'http://localhost:8080'
      : `https://${relevantSite}.carteav.com`;
  }, [relevantSite]);

  const [sitesList, setSiteList] = useState(null);
  const [sitesSockectsConnection, setSitesSockectsConnection] = useState(null);
  const [sitesEventsMap, setSitesEventsMap] = useState({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [configData, setConfigData] = useState({});

  // Additional states for fetched data (moved from SiteOverview)
  const [allExistsCarts, setAllExistsCarts] = useState(null);
  const [cartsLocationsArr, setCartsLocationsArr] = useState(null);
  const [cartsPaths, setCartsPaths] = useState(null);
  const [cartsData, setCartsData] = useState(null);
  const [statusCounters, setStatusCounters] = useState(null);

  const [cartsLocationObject, setCartsLocationObject] = useState(null);
  const [onlineCarts, setOnlineCarts] = useState([]);
  const [filterParam, setFilterParam, refFilterParam] = useStateAndRef([]);
  const [cartToShowPath, setCartToShowPath, refCartToShowPath] = useStateAndRef([]);
  const [selectedCart, setSelectedCart] = useState(null); // New state for focus 

  const [isPhoneSize, setIsPhoneSize] = useState(window.innerWidth < 768);
  const [eventsContainerOpen, setEventsContainerOpen] = useState(window.innerWidth > 768)
  // bump this every time the relevant site's socket (re)connects
  const [socketConnectKey, setSocketConnectKey] = useState(0);


  // bounderiesStorageResponse,
  // roadsResponse,
  // POISResponse,
  // CategoriesResponse,
  // memosResponse,

  const [bounderiesStorage, setBounderiesStorage] = useState(null);
  const [roadsStorage, setRoadsStorage] = useState(null);
  const [POISStorage, setPOISStorage] = useState(null);
  const [CategoriesStorage, setCategoriesStorage] = useState(null);
  const [memosStorage, setMemosStorage] = useState(null);
  const [roadLiveRecordings, setRoadLiveRecordings] = useState([]);
  const [socketsUpdated, setSocketsUpdated] = useState(false); // to trigger re-render on sockets update

  const enrichCartsLocationsArrWithSwVersion = (p_cartsLocationsArr, p_parsedResOnlineArr) => {
    if (!Array.isArray(p_cartsLocationsArr)) return p_cartsLocationsArr;
    if (!Array.isArray(p_parsedResOnlineArr) || p_parsedResOnlineArr.length === 0) {
      // keep array stable but ensure sw_version key exists (null) for consistent rendering
      return p_cartsLocationsArr.map((c) => ({ ...c, sw_version: c?.sw_version ?? null }));
    }

    const swByCartNumber = {};
    for (const item of p_parsedResOnlineArr) {
      const cn = Number(item?.cart_number);
      if (!Number.isFinite(cn)) continue;
      swByCartNumber[cn] = item?.sw_version ?? null;
    }

    return p_cartsLocationsArr.map((c) => {
      const cn = Number(c?.cart_number);
      const sw = Number.isFinite(cn) ? (swByCartNumber[cn] ?? null) : null;
      return { ...c, sw_version: sw };
    });
  };

  // Initialize multi-site sockets with voice layer enabled
  const { getSocket, emitAll, sockets, voiceSocketsApi, siteEvents } = useSockets({
    sites: sitesList || [],
    voice: true,
    voiceCallbacks: {},
    onSocketsUpdate: (map) => {
      setSitesSockectsConnection(map)
    },
    onEventsUpdate: (site, data) => {
      if (!site) return; // safety
      setSitesEventsMap(prev => {
        // removal case (data undefined)
        if (data === undefined) {
          const { [site]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [site]: data };
      });
    },
  });

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setWindowWidth(newWidth);
      setIsPhoneSize(newWidth < 768);

    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const getToken = async () => {
      try {
        const response = await RefreshToken();
        if (response) {
          setAuth(response);
        }
        setLoading(false);
        console.log("finish refresh token")
      } catch (error) { }
    }
    getToken();
  }, [])

  useEffect(() => {
    // when user change site , update the relevant states 
    if (sitesMapsDataObject && relevantSite && Object.prototype.hasOwnProperty.call(sitesMapsDataObject, relevantSite)) {
      setRelevantMapData(sitesMapsDataObject[relevantSite]);
    }
  }, [relevantSite, sitesMapsDataObject]);

  useEffect(() => {
    let intervalId;

    if (auth && Object.keys(auth).length > 0) {
      let developmentAuth = auth;

      // only for development
      if (window.location.href.includes('localhost')) {
        developmentAuth.relevant_site = 'localhost'
        // ,//'company'
        developmentAuth.sites_list = ['localhost'];
      }

      setSiteList(developmentAuth.sites_list);
      let currentRelevantSite = relevantSite;

      if (relevantSite == null) {
        currentRelevantSite = developmentAuth.relevant_site;
        setRelevantSite(developmentAuth.relevant_site);
      }
      setAuth(developmentAuth);
      setRole(auth.roles);


      setMapDataFromAllSitesFunction(developmentAuth.sites_list, currentRelevantSite);
    }

    return () => clearInterval(intervalId);

  }, [auth, relevantSite])

  const setMapDataFromAllSitesFunction = async (site_list) => {
    getMapDataObjectFromAllSites(site_list, setSitesMapsDataObject, relevantSite);
  };

  const fetchConfigData = async () => {
    try {
      const response = await axios.post(relevantUrl + '/admin/get_config_json');
      setConfigData(response.data);
    } catch (error) {
      console.error("Error fetching config data:", error);
    }
  };

  const getConfigValue = (key) => {
    for (const section in configData) {
      const paramsArray = configData[section];

      if (Array.isArray(paramsArray)) {
        for (const param of paramsArray) {
          if (param.name === key) {
            return param.value;
          }
        }
      }
    }
    return null;
  };

  const getConfigOptions = (key) => {
    for (const section in configData) {
      const paramsArray = configData[section];

      if (Array.isArray(paramsArray)) {
        for (const param of paramsArray) {
          if (param.name === key && param.options) {
            return param.options;
          }
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (relevantUrl) {
      fetchConfigData();
    }
  }, [relevantUrl]);





  const onReload = async (p_baseUrl) => {
    try {
      const [
        bounderiesStorageResponse,
        roadsResponse,
        POISResponse,
        CategoriesResponse,
        memosResponse,
      ] = await Promise.all([
        GetBounderiesStorage(p_baseUrl),
        GetRoadsStorage(p_baseUrl),
        GetPOIS(p_baseUrl),
        GetCategories(p_baseUrl),
        GetMemos(p_baseUrl),
      ]);

      setBounderiesStorage(bounderiesStorageResponse);
      setRoadsStorage(roadsResponse);
      setPOISStorage(POISResponse);
      setCategoriesStorage(CategoriesResponse);
      setMemosStorage(memosResponse);
    } catch (error) {
      console.error("onReload error:", error);
    }
  };

  useEffect(() => {
    if (relevantUrl) {
      onReload(relevantUrl);
    }
  }, [relevantUrl]);

  const filterCarts = (p_param = filterParam, p_cartsDataArray = cartsData) => {
    if (Array.isArray(p_cartsDataArray)) {
      const filteredCartsArray = p_cartsDataArray.filter((cart) =>
        p_param.includes(cart.status)
      );
      setCartsData(filteredCartsArray);
    }
    return null;
  };


  const reloadCartsData = async () => {
    if (!relevantUrl) return;
    try {
      const result = await axios.get(`${relevantUrl}/admin/getAllCartsData`);
      setStatusCounters(result.data?.counterStauses);

      // 6a) Take the raw response
      const cartsDataResponse = result.data?.cartDataCurrent;

      // 6b) If we have filterParam, apply filterCarts; otherwise just set
      if (cartsDataResponse && refFilterParam.current.length > 0) {
        if (filterParam.length > 0) {
          filterCarts(filterParam, cartsDataResponse);
        } else {
          setCartsData(cartsDataResponse);
        }
      }

      // 6c) Also set onlineCarts
      if (cartsDataResponse) {
        const newOnlineCarts = cartsDataResponse.filter((cart) => cart.isOnLine || cart.isOneWayConnection);
        setOnlineCarts(newOnlineCarts);
      } else {
        setOnlineCarts([]);
      }

      // 6d) Update cartsLocationObject
      setCartsLocationObject((prevCartsLocation) => {
        const newCartsLocation = result.data?.realTimelocationCartsObject;
        if (JSON.stringify(prevCartsLocation) !== JSON.stringify(newCartsLocation)) {
          return newCartsLocation;
        }
        return prevCartsLocation;
      });

      // 6e) Update allExistsCarts (offline carts?), cartsLocationsArr
      setAllExistsCarts((prevAllExistsCarts) => {
        const newAllExistsCarts = result.data?.parsedOfflineArr;
        if (JSON.stringify(prevAllExistsCarts) !== JSON.stringify(newAllExistsCarts)) {
          return newAllExistsCarts;
        }
        return prevAllExistsCarts;
      });

      setCartsLocationsArr((prevCartsLocationsArr) => {
        const newCartsLocationsArrRaw = result.data?.cartsLocationsArr;
        const newCartsLocationsArr = enrichCartsLocationsArrWithSwVersion(
          newCartsLocationsArrRaw,
          result.data?.parsedResOnlineArr
        );
        if (
          JSON.stringify(prevCartsLocationsArr) !==
          JSON.stringify(newCartsLocationsArr)
        ) {
          return newCartsLocationsArr;
        }
        return prevCartsLocationsArr;
      });
    } catch (err) {
      console.log("reloadCartsData error:", err);
      setCartsData(null);
      setOnlineCarts([]);
      setCartsLocationObject(null);
      setAllExistsCarts(null);
      setCartsLocationsArr(null);
    }
  };


  const reloadReservation = async () => {
    if (!relevantUrl) return;
    try {
      const res = await axios.get(`${relevantUrl}/admin/getAllFilteredActiveMissions`);
      setCartsPaths(res.data || {});

      if (res.data) {
        // Convert the keys of the returned object to an array
        let currentCartsPaths = Object.keys(res.data);

        // If we have a ref for cartToShowPath, filter out carts that no longer exist
        if (refCartToShowPath.current) {
          let cartToShowPathFiltered = refCartToShowPath.current.filter((cart) =>
            currentCartsPaths.includes(cart.toString())
          );
          setCartToShowPath(cartToShowPathFiltered);
        }
      } else {
        // If there's no data, and we previously had carts in cartToShowPath, clear them
        if (cartToShowPath.length > 0) {
          setCartToShowPath([]);
        }
      }
    } catch (err) {
      console.log("reloadReservation error:", err);
      setCartsPaths(null);

      // If an error occurs, optionally clear cartToShowPath
      setCartToShowPath([]);
    }
  };

  const reloadRoadLiveRecordings = async () => {
    if (!relevantUrl) return;
    try {
      const recordings = await getAllRoadLiveRecordings(relevantUrl);
      if (recordings !== 'error' && Array.isArray(recordings)) {
        // Only update if the data actually changed to prevent unnecessary re-renders
        setRoadLiveRecordings(prevRecordings => {
          if (JSON.stringify(prevRecordings) !== JSON.stringify(recordings)) {
            return recordings;
          }
          return prevRecordings;
        });
      }
    } catch (err) {
      console.log("reloadRoadLiveRecordings error:", err);
      // Don't clear recordings on error - keep the last valid state
    }
  };


  // re-run when the relevant site's socket connects again (new socket.id)
  useEffect(() => {
    if (!relevantSite) return;
    let isMounted = true;
    let s;

    const bump = () => {
      if (!isMounted) return;
      setSocketConnectKey((k) => k + 1);
    };

    (async () => {
      s = await getSocket(relevantSite);
      // If already connected when we attach, bump now
      if (s.connected) bump();
      // Bump on every future (re)connect as well
      s.on('connect', bump);
    })();

    return () => {
      isMounted = false;
      if (s) s.off('connect', bump);
    };
  }, [relevantSite, getSocket]);


  useEffect(() => {
    if (!relevantUrl || !relevantSite) return;

    // fetch static config via HTTP
    // onReload(relevantUrl);
    // reset some state (as you already do)
    setAllExistsCarts(null);
    setCartsLocationsArr(null);

    let cancelled = false;
    let s; // the socket we'll attach to

    // === handlers defined once so we can .off() them ===
    const handleReceiveActiveMissions = (missionsData, ack) => {
      const safeAck = (ok = true) => { try { typeof ack === "function" && ack(ok); } catch { } };

      if (cancelled) return safeAck(true);

      try {
        // console.log("Received Active Missions", missionsData);
        setCartsPaths(missionsData || {});
        if (missionsData) {
          const currentCartsPaths = Object.keys(missionsData);
          if (refCartToShowPath.current) {
            const filtered = refCartToShowPath.current.filter((cart) =>
              currentCartsPaths.includes(cart.toString())
            );
            setCartToShowPath(filtered);
          }
        } else if (cartToShowPath.length > 0) {
          setCartToShowPath([]);
        }

        safeAck(true);
      } catch (e) {
        console.error("handleReceiveActiveMissions failed:", e);
        safeAck(false);
      }
    };


    const handleReceiveActiveMissionsError = (error) => {
      if (cancelled) return;
      console.log("receiveActiveMissionsError:", error);
      setCartsPaths(null);
      setCartToShowPath([]);
    };

    // minimal change: add ack param + ack(true/false) with try/catch
    const handleReceiveCartData = (cartData, ack) => {
      const safeAck = (ok = true) => { try { typeof ack === "function" && ack(ok); } catch { } };

      if (cancelled || !cartData) return safeAck(true);

      try {
        setStatusCounters(cartData?.counterStatuses ?? cartData?.counterStauses);

        const cartsDataResponse = cartData?.cartDataCurrent;
        if (Array.isArray(cartsDataResponse) && refFilterParam.current.length > 0) {
          if (filterParam.length > 0) {
            filterCarts(filterParam, cartsDataResponse);
          } else {
            setCartsData(cartsDataResponse);
          }
        }

        if (Array.isArray(cartsDataResponse)) {
          const newOnlineCarts = cartsDataResponse.filter((cart) => cart.isOnLine || cart.isOneWayConnection);
          setOnlineCarts(newOnlineCarts);
        } else {
          setOnlineCarts([]);
        }

        setCartsLocationObject((prev) => {
          const nextObj = cartData?.realTimelocationCartsObject;
          return nextObj && JSON.stringify(prev) !== JSON.stringify(nextObj) ? nextObj : prev;
        });

        setAllExistsCarts((prev) => {
          const nextArr = cartData?.parsedOfflineArr;
          return nextArr && JSON.stringify(prev) !== JSON.stringify(nextArr) ? nextArr : prev;
        });

        setCartsLocationsArr((prev) => {
          const nextArrRaw = cartData?.cartsLocationsArr;
          const nextArr = enrichCartsLocationsArrWithSwVersion(nextArrRaw, cartData?.parsedResOnlineArr);
          return nextArr && JSON.stringify(prev) !== JSON.stringify(nextArr) ? nextArr : prev;
        });

        safeAck(true);
      } catch (e) {
        console.error("handleReceiveCartData failed:", e);
        safeAck(false);
      }
    };


    const handleReceiveCartDataError = (error) => {
      if (cancelled) return;
      console.log("receiveCartDataError:", error);
      setCartsData(null);
      setOnlineCarts([]);
      setCartsLocationObject(null);
      setAllExistsCarts(null);
      setCartsLocationsArr(null);
    };

    const handleReceiveRoadRecordings = (recordingsData, ack) => {
      const safeAck = (ok = true) => { try { typeof ack === "function" && ack(ok); } catch { } };

      if (cancelled) return safeAck(true);

      try {
        if (recordingsData !== 'error' && Array.isArray(recordingsData)) {
          setRoadLiveRecordings(prev =>
            JSON.stringify(prev) !== JSON.stringify(recordingsData) ? recordingsData : prev
          );
        }
        safeAck(true);
      } catch (e) {
        console.error("handleReceiveRoadRecordings failed:", e);
        safeAck(false);
      }
    };


    const handleReceiveRoadRecordingsError = (error) => {
      if (cancelled) return;
      console.log("receiveRoadRecordingsError:", error);
    };

    const register = () => {
      s.emit("registerToReceiveCartData");
      s.emit("registerToReceiveActiveMissions");
      s.emit("registerToReceiveRoadRecordings");
    };


    (async () => {
      s = await getSocket(relevantSite);

      // (Re)register on every connect (this covers reconnects too)

      if (s.connected) register();
      s.on('connect', register);

      // attach listeners
      s.on("receiveActiveMissions", handleReceiveActiveMissions);
      s.on("receiveActiveMissionsError", handleReceiveActiveMissionsError);

      s.on("receiveCartData", handleReceiveCartData);
      s.on("receiveCartDataError", handleReceiveCartDataError);

      s.on("receiveRoadRecordings", handleReceiveRoadRecordings);
      s.on("receiveRoadRecordingsError", handleReceiveRoadRecordingsError);
    })();

    return () => {
      cancelled = true;
      (async () => {
        if (!s) s = await getSocket(relevantSite).catch(() => null);
        if (!s) return;

        s.off('connect', register); // or keep a named handler and off that one specifically


        // detach listeners to avoid duplicates
        s.off("receiveActiveMissions", handleReceiveActiveMissions);
        s.off("receiveActiveMissionsError", handleReceiveActiveMissionsError);

        s.off("receiveCartData", handleReceiveCartData);
        s.off("receiveCartDataError", handleReceiveCartDataError);

        s.off("receiveRoadRecordings", handleReceiveRoadRecordings);
        s.off("receiveRoadRecordingsError", handleReceiveRoadRecordingsError);

        // optional: tell server to stop streaming
        if (s.connected) {
          s.emit("unregisterFromReceiveCartData");
          s.emit("unregisterFromReceiveActiveMissions");
          s.emit("unregisterFromReceiveRoadRecordings");
        }

        // remove the auto-register on future connects
      })();
    };
    // 👇 add the key here so effect re-runs on each socket (re)connect
  }, [relevantSite]);



  if (!loading) {
    return (
      <AuthContext.Provider
        value={{
          relevantMapData,
          setRelevantMapData,
          setSitesMapsDataObject,
          role,
          auth,
          setAuth,
          relevantUrl,
          sitesList,
          sitesSockectsConnection,
          setSitesSockectsConnection,
          relevantSite,
          setRelevantSite,
          sitesMapsDataObject,
          windowWidth,
          isPhoneSize,

          getConfigValue,
          getConfigOptions,
          fetchConfigData,
          getSocket,
          emitAll,
          sockets,
          voiceSocketsApi,
          sitesEventsMap,
          cartsData,
          statusCounters,
          allExistsCarts,
          cartsLocationsArr,
          cartsPaths,
          cartToShowPath,
          setCartToShowPath,
          refCartToShowPath,
          selectedCart,
          setSelectedCart,
          onReload,
          reloadCartsData,
          reloadReservation,
          onlineCarts,
          filterParam,
          refFilterParam,
          setFilterParam,
          cartsLocationObject,
          filterCarts,


          bounderiesStorage,
          roadsStorage,
          POISStorage,
          CategoriesStorage,
          memosStorage,
          roadLiveRecordings,
          reloadRoadLiveRecordings,


        }}
      >
        {children}
      </AuthContext.Provider>
    )
  } else {
    return null;
  }
}

export default AuthContext;